import React from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Navbar } from "./Navbar";
import { Footer } from "./Footer";
import { StickyQuoteButton } from "./StickyQuoteButton";
import { TrustBar } from "./TrustBar";
import { ExitIntentPopup } from "./ExitIntentPopup";
import { Schema } from "./SEO";
import { company } from "../data/company";

// NOTE: Aggregate review count is intentionally omitted from JSON-LD because
// we do not have a verified static number. Google's structured data guidelines
// require the aggregateRating.reviewCount to reflect real reviews. Once we
// receive a confirmed count from the Google Places API + Werkspot dashboard,
// re-enable aggregateRating below with the live values.

const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "GeneralContractor",
  name: company.name,
  image: `${company.url}/brand/logo.svg`,
  logo: `${company.url}/brand/logo.svg`,
  "@id": company.url,
  url: company.url,
  telephone: company.phone,
  email: company.email,
  priceRange: "€€€",
  address: {
    "@type": "PostalAddress",
    streetAddress: company.address.street || undefined,
    postalCode: company.address.postalCode || undefined,
    addressLocality: company.address.city,
    addressRegion: "Zuid-Holland",
    addressCountry: "NL",
  },
  areaServed: [
    "Maassluis", "Vlaardingen", "Schiedam", "Rotterdam", "Delft", "Westland",
    "Den Haag", "Rijswijk", "Zoetermeer", "Spijkenisse", "Capelle aan den IJssel",
    "Berkel en Rodenrijs", "Hoek van Holland", "Dordrecht", "Leiden", "Zuid-Holland",
  ],
  sameAs: [company.social.instagram, company.social.facebook, company.social.linkedin],
  vatID: company.btw,
  taxID: company.kvk,
  openingHoursSpecification: company.hours
    .filter((h) => h.time.includes("—") || h.time.includes("-"))
    .map((h) => ({
      "@type": "OpeningHoursSpecification",
      description: h.day,
      opens: h.time.split(" — ")[0] || h.time,
      closes: h.time.split(" — ")[1] || h.time,
    })),
};

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: company.name,
  url: company.url,
  logo: `${company.url}/brand/logo.svg`,
  contactPoint: [{
    "@type": "ContactPoint",
    telephone: company.phone,
    email: company.email,
    contactType: "customer service",
    areaServed: "NL",
    availableLanguage: ["Dutch", "Nederlands"],
  }],
  sameAs: [company.social.instagram, company.social.facebook, company.social.linkedin],
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
};

export const Layout = () => {
  return (
    <div className="min-h-screen bg-white text-m-text theme-modern">
      <Schema data={localBusinessSchema} id="local-business" />
      <Schema data={organizationSchema} id="organization" />
      <ScrollToTop />
      <Navbar />
      <main className="pt-[72px] md:pt-[80px]">
        <TrustBar />
        <Outlet />
      </main>
      <Footer />
      <StickyQuoteButton />
      <ExitIntentPopup />
    </div>
  );
};
